/*  ========================================================================
    # Component - Jobs
    ========================================================================  */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useTranslation from '../../hooks/useTranslation';
import JobFilter from './JobFilter.jsx';
import { AnimatePresence, motion } from 'motion/react';

const Jobs = ({
  variant,
  content,
  displayType,
  jobs,
  locationFilter,
  workingWorldFilter,
  maxInitialJobsShow
}) => {
  const __ = useTranslation();
  const [filteredJobs, setFilteredJobs] = useState(jobs || []);
  const [showAllJobs, setShowAllJobs] = useState(false);
  const [selectedWorkingWorld, setSelectedWorkingWorld] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const showFilters =
    (locationFilter && locationFilter.length > 0) ||
    (workingWorldFilter && workingWorldFilter.length > 0);

  const handleWorkingWorldFilterChange = selectedOption => {
    if (selectedOption && selectedOption.length > 0) {
      setSelectedWorkingWorld(selectedOption);
    } else {
      setSelectedWorkingWorld(null);
    }
  };

  const handleLocationFilterChange = selectedOption => {
    if (selectedOption && selectedOption.length > 0) {
      setSelectedLocation(selectedOption);
    } else {
      setSelectedLocation(null);
    }
  };

  // Update filtered jobs when filters change
  useEffect(() => {
    let updatedJobs = jobs;
    if (selectedWorkingWorld && selectedWorkingWorld.length > 0) {
      updatedJobs = updatedJobs.filter(
        job => job.workingWorldSlug && job.workingWorldSlug === selectedWorkingWorld
      );
    }

    if (selectedLocation && selectedLocation.length > 0) {
      updatedJobs = updatedJobs.filter(
        job => job.locationSlug && job.locationSlug === selectedLocation
      );
    }

    setFilteredJobs(updatedJobs);
  }, [jobs, selectedWorkingWorld, selectedLocation]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const locationParam = urlParams.get('location');
    const workingWorldParam = urlParams.get('working_world');

    if (locationParam) {
      setSelectedLocation(locationParam);
    }

    if (workingWorldParam) {
      setSelectedWorkingWorld(workingWorldParam);
    }
  }, []);

  return (
    <div className="container">
      <div className="s-jobs__wrapper">
        {content && (
          <div className="s-jobs__content">
            <div className="wysiwyg-block" dangerouslySetInnerHTML={{ __html: content }} />
          </div>
        )}

        <div className="s-jobs__list-wrapper">
          {showFilters && (
            <form className="s-jobs__filters">
              {locationFilter && locationFilter.length > 0 && (
                <JobFilter
                  selectedOption={selectedLocation}
                  filter={locationFilter}
                  label={__('jobs-filter-label-location')}
                  defaultOptionText={__('jobs-filter-location-default')}
                  onChange={filter => handleLocationFilterChange(filter)}
                />
              )}
              {workingWorldFilter && workingWorldFilter.length > 0 && (
                <JobFilter
                  selectedOption={selectedWorkingWorld}
                  filter={workingWorldFilter}
                  label={__('jobs-filter-label-wokringworld')}
                  defaultOptionText={__('jobs-filter-workingworld-default')}
                  onChange={filter => handleWorkingWorldFilterChange(filter)}
                />
              )}
            </form>
          )}

          <div className="s-jobs__list">
            <AnimatePresence>
              {filteredJobs && filteredJobs.length > 0 ? (
                (showAllJobs ? filteredJobs : filteredJobs.slice(0, maxInitialJobsShow)).map(
                  job => (
                    <motion.div
                      layout
                      key={`${job.jobID}-${job.jobArea}-${job.post.ID}`}
                      className="s-jobs__list-item-wrapper"
                    >
                      <a
                        className="s-jobs__item"
                        href={job.link ? job.link : '#contact'}
                        target={job.link ? '_blank' : '_self'}
                        rel="noreferrer"
                      >
                        <div className="s-jobs__item-meta u-subheadline">
                          {job.post.post_type === 'pm_job'
                            ? __('jobs-job-type-label')
                            : __('jobs-training-job-type-label')}
                        </div>
                        <h4 className="s-jobs__item-title">{job.title}</h4>
                        <div className="s-jobs__item-append">
                          <span>
                            <i className="u-icon u-icon--location-pin"></i>
                            {job.location}
                          </span>
                          <span>
                            <i className="u-icon u-icon--bag"></i>
                            {job.workingWorld}
                          </span>
                        </div>
                      </a>
                    </motion.div>
                  )
                )
              ) : (
                <motion.div layout>
                  <p className="s-jobs__list-no-found">{__('jobs-no-jobs-found')}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {!showAllJobs && filteredJobs.length > maxInitialJobsShow && (
            <div className="s-jobs__pagination">
              <button
                type="button"
                className="s-jobs__pagination-button c-button"
                onClick={() => setShowAllJobs(true)}
              >
                {__('jobs-load-all-button-label', { count: filteredJobs.length })}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

Jobs.propTypes = {
  variant: PropTypes.string,
  content: PropTypes.string,
  displayType: PropTypes.string,
  jobs: PropTypes.array,
  locationFilter: PropTypes.array,
  workingWorldFilter: PropTypes.array,
  maxInitialJobsShow: PropTypes.number
};

export default Jobs;
